import { CheckCircleFilled } from '@ant-design/icons';
import { InputNumber } from 'antd';
import AntFormItem from 'antd/lib/form/FormItem';
import { BoxNumber } from 'components/ui/base/box-number/box-number.styles';
import Input from 'components/ui/base/input/Input';
import styled, { css } from 'styled-components';
import { colors, fonts } from 'theme';

interface ErrorProp {
    error: boolean;
    touched: boolean;
    condensed?: boolean;
}

export const RefDiv = styled(BoxNumber)`
    margin-left: 0.8rem;
`;

export const InputLabel = styled.label`
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    white-space: nowrap;
`;

export const ErrorLabel = styled.label`
    color: var(--error-normal);
    font-weight: ${fonts.weight.regular};
    font-size: 1.4rem;
`;

export const SuccessLabel = styled.label`
    color: ${colors.success};
    font-weight: ${fonts.weight.regular};
    font-size: 1.4rem;
`;

export const StyledInput = styled(Input).withConfig({
    shouldForwardProp: (prop) => !['showSuccess', '$noPrefixGap'].includes(prop),
})<{ showSuccess: boolean; condensed?: boolean; $noPrefixGap?: boolean }>`
    width: 100%;
    border-radius: ${({ condensed }) => (condensed ? '4px' : '6px')};
    border-color: ${({ condensed }) => (condensed ? '#cecece' : 'var(--gray-5)')};
    height: 32px;
    padding: 0 12px;
    font-size: 14px;
    background-color: var(--gray-1);

    &.ant-input-affix-wrapper:not(.ant-input-affix-wrapper-disabled) {
        &:focus,
        &:active {
            border: 1px solid var(--brand-6);
            box-shadow: 0px 0px 4px 0px var(--box-shadow-1);
        }

        &:hover {
            border: 1px solid var(--brand-5);
        }
    }

    &.ant-input-affix-wrapper-focused {
        border: 1px solid var(--brand-6);
        box-shadow: 0px 0px 4px 0px var(--box-shadow-1);
    }

    &.ant-input-affix-wrapper-status-error {
        border: 1px solid #ff4d4f !important;
        outline: none !important;
        box-shadow: none !important;

        &:focus,
        &.ant-input-affix-wrapper-focused {
            border: 1px solid #ff4d4f !important;
            box-shadow: var(--error-box-shadow) !important;
            outline: none !important;
        }
    }

    &.ant-input-affix-wrapper-disabled,
    & input.ant-input-disabled {
        background-color: var(--gray-3);
        border-color: var(--gray-5);
        color: var(--gray-6);
        cursor: not-allowed;
        box-shadow: none !important;
        opacity: 1;
    }

    &.ant-input-affix-wrapper-disabled input::placeholder {
        color: var(--gray-6);
    }

    ${({ $noPrefixGap }) =>
        $noPrefixGap &&
        css`
            &&.ant-input-affix-wrapper .ant-input-prefix {
                margin-right: 0 !important;
                margin-inline-end: 0 !important;
            }
        `}
`;

export const FormItem = styled(AntFormItem)`
    width: 100%;
    margin-bottom: 0;
    max-width: 489px;
`;

export const InputDiv = styled.div`
    width: 100%;
    display: inline-flex;
`;

export const ErrorDiv = styled.div<ErrorProp>`
    width: 100%;
    margin-bottom: ${({ error, touched, condensed }) => (error && touched ? '0rem' : condensed ? '0' : '2.2rem')};
`;

export const SuccessDiv = styled.div`
    width: 100%;
    margin-top: 0.2rem;
    margin-bottom: 2.4rem;
`;

export const SuccessIcon = styled(CheckCircleFilled)`
    color: ${colors.success};
`;

export const Wrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

interface StyledNumberInputProps {
    $hideControls?: boolean;
    $controlsLeft?: boolean;
}

export const StyledNumberInput = styled(InputNumber)<StyledNumberInputProps>`
    width: 100%;
    border-radius: 6px;
    display: block;
    background-clip: padding-box;

    &.ant-input-number {
        border-color: var(--gray-5);
        height: 32px; /* wrapper */
    }

    .ant-input-number-input-wrap,
    .ant-input-number-input {
        border-radius: inherit;
        background-clip: padding-box;
    }

    .ant-input-number-handler-wrap {
        border-radius: 0 6px 6px 0;
    }

    &.ant-input-number:not(.ant-input-number-disabled):hover {
        border-color: var(--brand-5);
    }

    &.ant-input-number-focused {
        border-color: var(--brand-6);
        box-shadow: 0 0 4px 0 var(--box-shadow-1);
        outline: none;
    }

    /* error state */
    &.ant-input-number-status-error {
        border-color: var(--error-normal) !important;
        box-shadow: none !important;

        &.ant-input-number-focused {
            box-shadow: var(--error-box-shadow) !important;
        }
    }

    /* --- hide controls --- */
    ${({ $hideControls }) =>
        $hideControls &&
        css`
            .ant-input-number-handler-wrap {
                display: none !important;
            }
            .ant-input-number-input {
                text-align: right;
                padding-right: 8px;
            }
        `}
`;
